import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, WhatsappLogo } from "@phosphor-icons/react";
import { Reveal } from "../components/Reveal";
import { Wordmark } from "../components/marketing/Wordmark";
import { fmtDate } from "../components/marketing/Insights";
import { BlogHeader, BlogFooter } from "./Articles";
import { useLanguage } from "../context/LanguageContext";
import { WHATSAPP_URL } from "../i18n/content";

export default function ArticleDetail() {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const article = t.articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (article) {
      document.title = `${article.title} — PT Darma Daya Guna`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", article.excerpt);
    }
    return () => {
      document.title = "PT Darma Daya Guna — Training & Consultant";
    };
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-brand-paper font-satoshi flex flex-col items-center justify-center gap-6 px-6" data-testid="article-not-found">
        <Wordmark size="text-4xl" />
        <p className="text-black/60">{t.blog.notFound}</p>
        <Link
          data-testid="article-not-found-back"
          to="/artikel"
          className="flex items-center gap-2 bg-brand-ink text-brand-paper px-6 py-3 font-cabinet font-bold hover:bg-brand-green transition-colors duration-300"
        >
          <ArrowLeft size={16} weight="bold" />
          {t.blog.back}
        </Link>
      </div>
    );
  }

  const others = t.articles.filter((a) => a.slug !== article.slug);

  return (
    <div className="grain bg-brand-paper text-brand-ink font-satoshi min-h-screen" data-testid="article-detail-page">
      <BlogHeader backTo="/artikel" backLabel={t.blog.back} />

      <main className="max-w-[820px] mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-20 md:pb-28">
        <Reveal>
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-black/45 mb-8">
            <Link to="/" className="hover:text-black transition-colors">DDG</Link>
            <span className="mx-2 text-brand-red">/</span>
            <Link to="/artikel" className="hover:text-black transition-colors">{t.nav.articles}</Link>
            <span className="mx-2 text-brand-red">/</span>
            <span className="text-brand-green">{article.category}</span>
          </p>
          <div className="flex items-center gap-3 mb-6">
            <span className="bg-brand-green/10 text-brand-green px-3 py-1 text-[10px] font-bold tracking-[0.15em] uppercase">
              {article.category}
            </span>
            <span className="text-xs text-black/40" data-testid="article-meta">
              {fmtDate(article.date, lang)} · {article.readTime} {t.insights.minRead}
            </span>
          </div>
          <h1 data-testid="article-title" className="font-cabinet font-black tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.08]">
            {article.title}
          </h1>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-black/55 border-l-2 border-brand-green pl-5">
            {article.excerpt}
          </p>
        </Reveal>

        <article className="mt-12 md:mt-16" data-testid="article-body">
          {article.blocks.map((b, i) =>
            b.h ? (
              <Reveal key={i}>
                <h2 className="font-cabinet font-bold text-xl sm:text-2xl tracking-tight mt-12 mb-4">{b.h}</h2>
              </Reveal>
            ) : (
              <Reveal key={i}>
                <p className="text-base leading-relaxed text-black/70 mb-6">{b.p}</p>
              </Reveal>
            )
          )}
        </article>

        <Reveal>
          <div className="mt-16 border border-black/15 bg-white p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between">
            <h2 className="font-cabinet font-black tracking-tight text-xl sm:text-2xl max-w-md">
              {t.contact.title}
            </h2>
            <div className="flex flex-wrap gap-3">
              <Link
                data-testid="article-cta-contact"
                to="/#contact"
                className="group flex items-center gap-2 bg-brand-green text-white px-6 py-3.5 font-cabinet font-bold text-sm tracking-wide hover:bg-brand-ink transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-brand-green"
              >
                {t.nav.cta}
                <ArrowUpRight size={15} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a
                data-testid="article-cta-whatsapp"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 border border-black/25 px-6 py-3.5 font-cabinet font-bold text-sm tracking-wide text-black/80 hover:border-brand-green hover:text-brand-green transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-brand-green"
              >
                <WhatsappLogo size={18} weight="duotone" />
                {t.contact.whatsappCta}
              </a>
            </div>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-16">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-black/45 mb-6">
              — {t.blog.related}
            </p>
            <div className="flex flex-col border-t border-black/10">
              {others.map((a) => (
                <Link
                  key={a.slug}
                  data-testid={`related-article-${a.slug}`}
                  to={`/artikel/${a.slug}`}
                  className="group flex items-center justify-between gap-6 py-6 border-b border-black/10 px-2 md:px-4 transition-colors duration-300 hover:bg-black/[0.02] focus:outline-none focus:ring-2 focus:ring-brand-green"
                >
                  <div>
                    <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-brand-green">{a.category}</span>
                    <h3 className="font-cabinet font-bold text-lg md:text-xl tracking-tight mt-1 group-hover:text-brand-green transition-colors duration-300">
                      {a.title}
                    </h3>
                  </div>
                  <ArrowUpRight size={20} weight="bold" className="text-black/30 shrink-0 transition-[color,transform] duration-300 group-hover:text-brand-red group-hover:translate-x-1 group-hover:-translate-y-1" />
                </Link>
              ))}
            </div>
          </div>
        </Reveal>
      </main>
      <BlogFooter />
    </div>
  );
}
